import React from "react";
import { Link } from "react-router-dom";
import { Landmark, Phone, Mail, MapPin } from "lucide-react";
import { useI18n } from "@/i18n";

export default function Footer() {
  const { t, tRaw, buildPath, lang } = useI18n();
  const legal = tRaw("footer.legalDetails") || {};

  const scrollTo = (id) => {
    const isHome = window.location.pathname === `/${lang}`;
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = `${buildPath("home")}#${id}`;
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A192F] text-slate-300 pt-14 pb-8"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand column */}
          <div>
            <Link
              to={buildPath("home")}
              data-testid="footer-brand"
              className="flex items-center gap-3"
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[#C5A059] text-[#0F2C59]">
                <Landmark size={20} strokeWidth={2.2} />
              </span>
              <span className="font-serif text-xl font-semibold text-white">
                {t("footer.brand")}
              </span>
            </Link>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              {t("footer.description")}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-4">
              {t("footer.sections.navigation")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={buildPath("home")}
                  data-testid="footer-link-home"
                  className="hover:text-white transition-colors"
                >
                  {t("footer.links.home")}
                </Link>
              </li>
              <li>
                <button
                  data-testid="footer-link-services"
                  onClick={() => scrollTo("services")}
                  className="hover:text-white transition-colors text-left"
                >
                  {t("footer.links.services")}
                </button>
              </li>
              <li>
                <button
                  data-testid="footer-link-faq"
                  onClick={() => scrollTo("faq")}
                  className="hover:text-white transition-colors text-left"
                >
                  {t("footer.links.faq")}
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-4">
              {t("footer.sections.legal")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={buildPath("privacy")}
                  data-testid="footer-privacy-link"
                  className="hover:text-white transition-colors"
                >
                  {t("footer.links.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={buildPath("cookies")}
                  data-testid="footer-cookies-link"
                  className="hover:text-white transition-colors"
                >
                  {t("footer.links.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={buildPath("terms")}
                  data-testid="footer-terms-link"
                  className="hover:text-white transition-colors"
                >
                  {t("footer.links.terms")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-4">
              {t("footer.sections.contact")}
            </h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 text-[#C5A059] shrink-0" />
                <span>{legal.address}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-[#C5A059] shrink-0" />
                <a
                  href={`tel:${(legal.phone || "").replace(/\s+/g, "")}`}
                  data-testid="footer-phone"
                  className="hover:text-white"
                >
                  {legal.phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-[#C5A059] shrink-0" />
                <a
                  href={`mailto:${legal.email}`}
                  data-testid="footer-email"
                  className="hover:text-white"
                >
                  {legal.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal details block */}
        <div className="mt-10 pt-8 border-t border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-slate-400 leading-relaxed">
          <div>
            <p className="text-white font-semibold text-sm mb-2">
              {legal.companyName}
            </p>
            <p>
              <span className="text-slate-500">{legal.rfcLabel}</span>{" "}
              {legal.rfc}
            </p>
            <p>
              <span className="text-slate-500">{legal.folioLabel}</span>{" "}
              {legal.folio}
            </p>
            <p>
              <span className="text-slate-500">{legal.licenseLabel}</span>{" "}
              {legal.license}
            </p>
            <p>
              <span className="text-slate-500">{legal.incorporationLabel}</span>{" "}
              {legal.incorporation}
            </p>
            <p>
              <span className="text-slate-500">{legal.jurisdictionLabel}</span>{" "}
              {legal.jurisdiction}
            </p>
          </div>
          <div className="italic text-slate-500">{legal.disclaimer}</div>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-800 text-xs text-slate-500 text-center">
          {t("footer.copyright")}
        </div>
      </div>
    </footer>
  );
}
